/**
 * Phases Hooks
 *
 * Manages treatment phases via the real API.
 * Phases correspond to treatment steps (pre_treatment, protocol, egg_retrieval, etc.)
 * and are stored in the `phases` table linked to cycles.
 *
 * Phase completion is tracked by setting `endDate`:
 * - No endDate = in progress / pending
 * - endDate = startDate = skipped
 * - endDate != startDate = completed
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { cycleKeys } from "@src/packages/cycles/cycle-cache-keys";
import { useApiClient } from "@src/packages/api/louise-api";
import { throwApiError } from "@src/packages/api/api-error";
import type { TreatmentStepConfig } from "@src/packages/homepage/sections/TreatmentProgressModal";
import type { paths } from "@src/packages/openapi/schema";
import type { ResponseBody } from "@src/packages/openapi/helpers";
import {
  DEV_ACTIVE_CYCLE_OVERRIDE,
  MOCK_CYCLE_ID,
  MOCK_CYCLE_PHASES,
} from "@src/packages/cycles/dev/devActiveCycleOverride";
import { isDevNavigableCycleId } from "@src/packages/cycles/hooks/useCycles";
import {
  toCreatePhaseBody,
  toUpdatePhaseBody,
  type PhaseInseminationInfoInput,
  type PhaseProcedureInput,
  type PhaseSpermiogramInput,
  type PhaseSubObjects,
  type PhaseTestResult,
} from "./phase-request-mappers";
import { numericStepValue as numericValue } from "./numericStepValue";
import { resolvePhaseProcedureDateValue } from "./phaseProcedureDate";

import { buildEmbryoTransferInfo } from "@src/packages/patients/embryoTransferInfo";
import {
  EMBRYO_GRADES_FIELD,
  bestEmbryoGrade,
  buildEmbryoDataWrite,
  decodeEmbryoGrades,
  embryoGradesFromPhase,
  encodeEmbryoGrades,
} from "@src/packages/patients/embryoGrades";
import { resolveStepProgressStatus } from "@src/packages/cycles/presenters/stepProgressStatus";
import {
  analysisDataFromSpermiogram,
  analysisRowForDisplay,
  analysisRowOwnedByPatient,
  buildAnalysisSpermiogram,
} from "./analysisSpermiogram";
import {
  buildSpermiogramIdentity,
  spermiogramRowForDisplay,
  spermiogramRowOwnedByPatient,
} from "./spermiogramRows";
import { getTodayString, parseDateOrTimestamp } from "@src/packages/utils/date";

export type { PhaseTestResult };
export {
  buildPgtTestResult,
  pgtTestResultMatches,
  type PgtPersistenceStatus,
} from "./pgtResult";

// ============================================================================
// Types
// ============================================================================

/** Phase as returned from GET /{tenantId}/cycles/{id}/phases — derived from the OpenAPI schema. */
export type ApiPhase = ResponseBody<
  paths["/{tenantId}/cycles/{id}/phases"]["get"]
>["data"][number];

export type { ApiFertilityMetric } from "@src/packages/patients/cycleOutcomeMetrics";
export {
  buildFertilityMetrics,
  clearedOocyteCounts,
  STEP_FIELD_TO_METRIC,
} from "@src/packages/patients/cycleOutcomeMetrics";
export type { FertilityMetricWrite } from "@src/packages/patients/cycleOutcomeMetrics";

/** Derived step status from API phases.
 *  - pending: no phase, or phase with no startDate/endDate yet
 *  - scheduled: phase has startDate in the future, endDate is null
 *  - completed: phase has endDate strictly after startDate (and endDate ≤ today)
 *  - skipped: startDate === endDate (explicit skip)
 */
export interface DerivedStepProgress {
  stepId: string;
  /** Real UUID from the phases table — used to link medications */
  phaseId: string | null;
  status: "pending" | "scheduled" | "completed" | "skipped";
  completedAt: string | null;
  /** When status==='scheduled', the date the phase is planned for (YYYY-MM-DD). */
  scheduledFor: string | null;
  /** Egg counts and other procedure data derived from fertilityMetrics */
  data: Record<string, string | number | boolean | null>;
}

// ============================================================================
// Step-to-Phase-Type Mapping
// ============================================================================

/**
 * Maps treatment step IDs to API phase type strings.
 * Step IDs come from TREATMENT_FLOWS in TreatmentProgressModal,
 * and must match the `type` field on the phases table.
 */
const STEP_TO_PHASE_TYPE: Record<string, string> = {
  pre_treatment: "pre_treatment",
  protocol: "protocol",
  stimulation: "protocol",
  trigger_shot: "protocol",
  egg_retrieval: "egg_retrieval",
  freezing: "egg_retrieval", // Outcome step — mature/frozen eggs saved on egg_retrieval phase
  fertilization: "fertilization",
  embryo_development: "embryo_development",
  // The current API has no standalone PGT phase. Keep the optional PGT record
  // on the embryo-development phase until the typed embryo/report contract is
  // available; the PGT status is derived from its test result, never from the
  // shared phase dates.
  pgt_testing: "embryo_development",
  transfer: "transfer",
  luteal_phase: "luteal_phase",
  // The wait and the result share the luteal phase's dates — the API has no
  // outcome phase, and the result itself is the cycle's outcome record rather
  // than anything held on a phase.
  outcome: "luteal_phase",
  sperm_collection: "sperm_collection",
  insemination: "insemination",
  // Sperm freezing flow: screening lives on the protocol phase (pre-collection
  // workup), analysis on the fertilization phase (lab analysis of the sample).
  screening: "protocol",
  analysis: "fertilization",
};

/**
 * Maps API fertilityMetrics keys to step data field names.
 * Used when deriving step data from saved phase metrics.
 */
/** Maps API metric type → step field name, per step ID.
 * Some metric types map to different fields depending on the step. */
const METRIC_TO_STEP_FIELD: Record<string, string> = {
  retrieved_oocytes: "eggsCollected",
  m2_count: "matureEggs",
  immature_oocytes: "immatureMI",
  degenerated_oocytes: "degenerated",
  frozen_embryo: "eggsFrozen", // default — overridden per-step in deriveStepProgress
  injected_oocytes: "eggsInjected",
  fertilized_oocytes: "eggsFertilized",
  transferred_embryo: "embryosTransferred",
  embryos_day_3: "embryosDay3",
  embryos_day_5: "embryosDay5",
};

/**
 * Maps step data field names to API fertilityMetrics keys.
 * Used when saving step completion data to a phase.
 */
// ============================================================================
// Helpers
// ============================================================================

/**
 * Get the API phase type string for a given step ID.
 * Returns null if no mapping exists.
 */
export function getPhaseTypeForStep(stepId: string): string | null {
  return STEP_TO_PHASE_TYPE[stepId] ?? null;
}

/**
 * Build fertilityMetrics object from step data for egg retrieval phases.
 * Returns undefined if no metrics are present.
 */
/**
 * Build phase sub-objects from step data for saving to the API.
 * Maps step fields to their corresponding nested API sub-objects
 * (spermiograms, inseminationInfo, eggRetrievalInfo, embryoData).
 */

type DateTimeParts = {
  date: string | null;
  time: string | null;
};

function splitDateTime(value: unknown): DateTimeParts {
  if (typeof value !== "string" || value.trim() === "") {
    return { date: null, time: null };
  }
  const [datePart, timePart] = value.split("T");
  return {
    date: datePart || null,
    time: timePart ? timePart.slice(0, 5) : null,
  };
}

const SPERM_TECHNIQUE_MAP: Record<
  string,
  NonNullable<PhaseSpermiogramInput["technique"]>
> = {
  ejaculation: "ejaculate",
  ejaculate: "ejaculate",
  pesa: "pesa",
  tesa: "tesa",
  mtese: "mtese",
  electroejaculation: "electroejaculation",
  post_ejaculatory_urine: "post_ejaculatory_urine",
};

const SEMEN_PREPARATION_MAP: Record<
  string,
  NonNullable<PhaseSpermiogramInput["preparationMethod"]> | null
> = {
  none: null,
  washed: "simple_wash",
  swim_up: "swim_up",
  density_gradient: "dgc",
  dgc_swim_up: "dgc_swim_up",
  microfluidics: "microfluidics",
  macs: "macs",
};

const SPERM_TECHNIQUE_TO_FIELD: Record<string, string> = {
  ejaculate: "ejaculation",
};

const SEMEN_PREPARATION_TO_FIELD: Record<string, string> = {
  simple_wash: "washed",
  swim_up: "swim_up",
  dgc: "density_gradient",
  dgc_swim_up: "dgc_swim_up",
  microfluidics: "microfluidics",
  macs: "macs",
};

function phaseProcedure(
  phase: ApiPhase | null | undefined,
  type: PhaseProcedureInput["procedureType"],
): PhaseProcedureInput | undefined {
  return phase?.procedures?.find(
    (procedure) => procedure.procedureType === type,
  );
}

function addSpermiogramFields(
  target: Partial<PhaseSubObjects>,
  data: Record<string, string | number | boolean | null>,
  existingPhase: ApiPhase | null | undefined,
  patientId: string,
): void {
  const sperm: PhaseSpermiogramInput = buildSpermiogramIdentity(
    data,
    patientId,
  );
  const semenSpecimenId =
    typeof data.semenSpecimenId === "string" ? data.semenSpecimenId : undefined;
  const existing = spermiogramRowOwnedByPatient(
    existingPhase?.spermiograms,
    semenSpecimenId,
  );
  if (existing?.id) sperm.id = existing.id;
  if (data.collectionMethod) {
    const technique = SPERM_TECHNIQUE_MAP[String(data.collectionMethod)];
    if (technique) sperm.technique = technique;
  }
  // The current phase spermiogram contract does not expose abstinence. That
  // value is retained in the phase notes JSON and rehydrated below until the
  // backend exposes the semen-specimen write path for this field.
  if (typeof data.preparationMethod === "string") {
    sperm.preparationMethod =
      SEMEN_PREPARATION_MAP[String(data.preparationMethod)] ?? null;
  }
  const dateTime = splitDateTime(data.collectionDate ?? data.procedureDate);
  if (dateTime.date) sperm.date = dateTime.date;
  if (dateTime.time) sperm.time = dateTime.time;
  if (typeof data.notes === "string" && data.notes) sperm.notes = data.notes;
  target.spermiograms = [sperm];
}

function addProcedure(
  target: Partial<PhaseSubObjects>,
  data: Record<string, string | number | boolean | null>,
  existingPhase: ApiPhase | null | undefined,
  patientId: string,
  procedureType: NonNullable<PhaseProcedureInput["procedureType"]>,
): void {
  const dateTime = splitDateTime(data.collectionDate ?? data.procedureDate);
  const existing = phaseProcedure(existingPhase, procedureType);
  const appointmentId = Object.prototype.hasOwnProperty.call(
    data,
    "appointmentId",
  )
    ? typeof data.appointmentId === "string"
      ? data.appointmentId
      : null
    : (existing?.appointmentId ?? undefined);
  if (!dateTime.date && !dateTime.time && !data.notes && !appointmentId) return;
  target.procedures = [
    {
      ...(existing?.id ? { id: existing.id } : {}),
      patientId,
      ...(dateTime.date ? { date: dateTime.date } : {}),
      ...(dateTime.time ? { time: dateTime.time } : {}),
      procedureType,
      ...(appointmentId !== undefined && { appointmentId }),
      ...(typeof data.notes === "string" && data.notes.trim()
        ? { notes: data.notes }
        : {}),
    },
  ];
}

function addInseminationInfo(
  target: Partial<PhaseSubObjects>,
  data: Record<string, string | number | boolean | null>,
  existingPhase: ApiPhase | null | undefined,
): void {
  const dateTime = splitDateTime(data.procedureDate);
  const existing = existingPhase?.inseminationInfo;
  const info: PhaseInseminationInfoInput = {};
  if (existing?.id) info.id = existing.id;
  if (dateTime.date) info.date = dateTime.date;
  if (dateTime.time) info.time = dateTime.time;
  if (
    data.inseminationType === "single" ||
    data.inseminationType === "double"
  ) {
    info.inseminationType = data.inseminationType;
  }
  if (data.catheterType === "straight" || data.catheterType === "curved") {
    info.catheterType = data.catheterType;
  }
  if (typeof data.catheterBrand === "string" && data.catheterBrand) {
    info.catheterBrand = data.catheterBrand;
  }
  if (data.ultrasoundGuided != null && data.ultrasoundGuided !== "") {
    info.ultrasoundGuided =
      data.ultrasoundGuided === "yes" || data.ultrasoundGuided === true;
  }
  if (data.inseminationDuration != null && data.inseminationDuration !== "") {
    info.inseminationDuration = Number(data.inseminationDuration);
  }
  if (typeof data.notes === "string" && data.notes) info.notes = data.notes;
  target.inseminationInfo = info;
}

export function buildPhaseSubObjects(
  stepId: string,
  data: Record<string, string | number | boolean | null>,
  patientId: string,
  existingPhase?: ApiPhase | null,
  cycleType?: string | null,
): PhaseSubObjects {
  const result: PhaseSubObjects = {};

  if (stepId === "trigger_shot" && data.triggerDate) {
    const raw = String(data.triggerDate);
    // The picker emits a bare date when no time was entered. That is the
    // local day at 00:00, the same time the read-back below assumes, not UTC
    // midnight (the evening before, west of Greenwich).
    const d = parseDateOrTimestamp(raw);
    // Use local date methods — backend stores wall-clock time, not UTC
    const y = d.getFullYear();
    const mo = (d.getMonth() + 1).toString().padStart(2, "0");
    const da = d.getDate().toString().padStart(2, "0");
    const triggerObj: {
      id?: string;
      date: string;
      time?: string;
      notes?: string;
    } = {
      date: `${y}-${mo}-${da}`,
    };
    // Include existing ID so backend updates instead of inserting
    if (existingPhase?.triggerShot?.id) {
      triggerObj.id = existingPhase.triggerShot.id;
    }
    const hours = d.getHours().toString().padStart(2, "0");
    const minutes = d.getMinutes().toString().padStart(2, "0");
    triggerObj.time = `${hours}:${minutes}`;
    const med1 = data.triggerMedicationCustom
      ? String(data.triggerMedicationCustom)
      : data.triggerMedication
        ? String(data.triggerMedication)
        : null;

    const med2 = data.triggerMedicationCustom2
      ? String(data.triggerMedicationCustom2)
      : data.triggerMedication2 && data.triggerMedication2 !== "__pending__"
        ? String(data.triggerMedication2)
        : null;

    if (med1) {
      triggerObj.notes = med2 ? `${med1} + ${med2}` : med1;
    }
    result.triggerShot = triggerObj;
  }

  if (stepId === "sperm_collection") {
    addSpermiogramFields(result, data, existingPhase, patientId);
    addProcedure(result, data, existingPhase, patientId, "sperm_collection");
  }

  if (stepId === "analysis") {
    // Her own row, never whatever came back first. With no row of hers the id
    // is omitted and the phase write inserts one beside the clinic's, which the
    // per-item nested write leaves untouched.
    const sperm = buildAnalysisSpermiogram(
      data,
      patientId,
      analysisRowOwnedByPatient(existingPhase?.spermiograms)?.id,
    );
    if (sperm) result.spermiograms = [sperm];
  }

  if (stepId === "fertilization") {
    const i: Partial<PhaseInseminationInfoInput> = {};
    // Include existing inseminationInfo ID
    if (existingPhase?.inseminationInfo?.id) {
      i.id = existingPhase.inseminationInfo.id;
    }
    if (
      data.fertilizationMethod === "ivf" ||
      data.fertilizationMethod === "icsi"
    ) {
      i.technique = data.fertilizationMethod;
    }
    if (typeof data.notes === "string" && data.notes) i.notes = data.notes;
    // Sperm identity for the fertilization step is written through the same
    // spermiogram path as insemination so that a linked cryo-vault sample
    // (semenSpecimenId) and spermType round-trip on save — a bespoke
    // origin-only writer used to drop both.
    if (data.semenSpecimenId || data.spermSource || data.spermType) {
      addSpermiogramFields(result, data, existingPhase, patientId);
    }
    if (Object.keys(i).filter((k) => k !== "id").length || i.id) {
      result.inseminationInfo = i;
    }
  }

  if (stepId === "insemination") {
    // Always create inseminationInfo for insemination steps so the backend
    // has a record even when no notes are entered
    addInseminationInfo(result, data, existingPhase);
    if (data.semenSpecimenId || data.spermSource || data.spermType) {
      addSpermiogramFields(result, data, existingPhase, patientId);
    }
    addProcedure(result, data, existingPhase, patientId, "insemination");
  }

  if (stepId === "egg_retrieval" && data.notes) {
    const eggObj: Record<string, unknown> = { notes: data.notes as string };
    if (existingPhase?.eggRetrievalInfo?.id) {
      eggObj.id = existingPhase.eggRetrievalInfo.id;
    }
    result.eggRetrievalInfo = eggObj;
  }

  if (stepId === "embryo_development" || stepId === "fertilization") {
    // One row per embryo, from the list the form edits. Data saved before
    // there was a list carries one grade, which stays the phase's first row.
    const embryos = decodeEmbryoGrades(data[EMBRYO_GRADES_FIELD]);
    if (embryos) {
      const embryoData = buildEmbryoDataWrite(
        embryos,
        existingPhase?.embryoData ?? [],
      );
      if (embryoData.length > 0) result.embryoData = embryoData;
    } else if (typeof data.embryoGrade === "string" && data.embryoGrade) {
      const firstId = existingPhase?.embryoData?.[0]?.id;
      result.embryoData = [
        firstId
          ? { id: firstId, grade: data.embryoGrade }
          : { grade: data.embryoGrade },
      ];
    }
  }

  if (stepId === "transfer") {
    // FET is a frozen embryo transfer by definition, so the patient form
    // no longer surfaces the fresh/frozen selector. Persist the value
    // explicitly here so the phase record stays complete regardless.
    const transferData =
      cycleType === "fet" ? { ...data, embryoType: "frozen" } : data;
    if (
      Object.prototype.hasOwnProperty.call(transferData, "embryoType") ||
      Object.prototype.hasOwnProperty.call(transferData, "notes")
    ) {
      result.embryoTransferInfo = buildEmbryoTransferInfo(
        transferData,
        existingPhase?.embryoTransferInfo,
      );
    }
  }

  return result;
}

/**
 * For steps that have no fertilityMetrics / sub-object mapping (e.g.
 * sperm_freezing's freezing and analysis steps), we stash their step data
 * as a JSON blob on phase.notes. This helper parses that blob back and
 * merges it into the derived data object so the form's local state
 * repopulates after a refetch.
 */
function mergeNotesJson(
  data: Record<string, string | number | boolean | null>,
  notes: string | null | undefined,
): Record<string, string | number | boolean | null> {
  if (!notes) return data;
  try {
    const parsed = JSON.parse(notes);
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
      return {
        ...data,
        ...(parsed as Record<string, string | number | boolean | null>),
      };
    }
  } catch {
    // notes wasn't JSON — leave data alone
  }
  return data;
}

/**
 * Derive step progress from API phases.
 *
 * Phase status rules:
 * - No matching phase → "pending"
 * - Phase with no endDate → "pending" (in progress, shown as active)
 * - Phase with startDate === endDate → "skipped" (set explicitly by handleSkipStep)
 * - Phase with startDate < endDate → "completed" (handleCompleteStep always ensures this)
 */
export function deriveStepProgress(
  phases: ApiPhase[],
  steps: TreatmentStepConfig[],
): DerivedStepProgress[] {
  const todayIso = getTodayString();
  return steps.map((step) => {
    const phaseType = getPhaseTypeForStep(step.id);
    const phase = phaseType ? phases.find((p) => p.type === phaseType) : null;

    // A step whose progress the cycle decides, not the patient, takes nothing
    // from the phase it borrows — see `resolveStepProgressStatus`.
    if (step.derivedProgress) {
      return {
        stepId: step.id,
        phaseId: null,
        status: "pending" as const,
        completedAt: null,
        scheduledFor: null,
        data: {},
      };
    }

    if (!phase) {
      return {
        stepId: step.id,
        phaseId: null,
        status: "pending",
        completedAt: null,
        scheduledFor: null,
        data: {},
      };
    }

    const { status: resolvedStatus, scheduledFor } = resolveStepProgressStatus({
      stepId: step.id,
      derivedProgress: step.derivedProgress,
      stepIds: steps,
      phase,
      todayIso,
    });
    const isSkipped = resolvedStatus === "skipped";
    const isCompleted = resolvedStatus === "completed";
    const isScheduled = resolvedStatus === "scheduled";

    // Map fertilityMetrics array to step data fields
    const data: Record<string, string | number | boolean | null> = {};
    if (Array.isArray(phase.fertilityMetrics)) {
      for (const metric of phase.fertilityMetrics) {
        if (metric.type) {
          let stepField = METRIC_TO_STEP_FIELD[metric.type];
          // frozen_embryo maps to different field names per step
          if (
            metric.type === "frozen_embryo" &&
            step.id === "embryo_development"
          ) {
            stepField = "embryosFrozen";
          }
          if (stepField) {
            data[stepField] = metric.value != null ? metric.value : null;
          }
        }
      }
    }
    // Read sub-object fields back from the API response
    const sperm = spermiogramRowForDisplay(phase.spermiograms);
    if (sperm) {
      if (sperm.origin) {
        data["spermSource"] = sperm.origin;
      }
      if (sperm.spermType) {
        data["spermType"] = sperm.spermType;
      }
      if (sperm.semenSpecimenId) {
        data["semenSpecimenId"] = sperm.semenSpecimenId;
      }
      if (sperm.technique && step.id === "sperm_collection") {
        data["collectionMethod"] =
          SPERM_TECHNIQUE_TO_FIELD[sperm.technique] ?? sperm.technique;
      }
      if (sperm.preparationMethod && step.id === "sperm_collection") {
        data["preparationMethod"] =
          SEMEN_PREPARATION_TO_FIELD[sperm.preparationMethod] ??
          sperm.preparationMethod;
      }
      if (sperm.date && step.id === "sperm_collection") {
        data["collectionDate"] = sperm.time
          ? `${sperm.date}T${sperm.time}:00`
          : sperm.date;
      }
      if (sperm.notes && step.id === "sperm_collection") {
        data["notes"] = sperm.notes;
      }
    }
    /**
     * The appointment this step's procedure was scheduled through.
     *
     * `addProcedure` has persisted `appointmentId` since the scheduling flow was
     * built, and nothing read it back — so on every reload the step forgot which
     * calendar entry it owned. That was harmless while only the appointment flow
     * wrote dates, and it stops being harmless the moment the step's own modal
     * can move that date: without this, a corrected date books a second
     * appointment instead of moving the first.
     *
     * Only `sperm_collection` and `insemination` write a procedure, each under a
     * `procedureType` equal to its step id.
     */
    if (step.id === "sperm_collection" || step.id === "insemination") {
      const stepProcedure = phase.procedures?.find(
        (procedure) => procedure.procedureType === step.id,
      );
      if (stepProcedure?.appointmentId) {
        data["appointmentId"] = stepProcedure.appointmentId;
      }
    }

    if (step.id === "insemination") {
      const groupSpecimen = phase.inseminationGroups?.flatMap(
        (group) => group.semenSpecimens ?? [],
      )[0];
      if (groupSpecimen?.id && !data.semenSpecimenId) {
        data.semenSpecimenId = groupSpecimen.id;
      }
    }
    if (phase.inseminationInfo) {
      if (phase.inseminationInfo.technique && step.id === "fertilization") {
        data["fertilizationMethod"] = phase.inseminationInfo.technique;
      }
      if (
        phase.inseminationInfo.inseminationType &&
        step.id === "insemination"
      ) {
        data["inseminationType"] = phase.inseminationInfo.inseminationType;
      }
      if (phase.inseminationInfo.catheterType && step.id === "insemination") {
        data["catheterType"] = phase.inseminationInfo.catheterType;
      }
      if (phase.inseminationInfo.catheterBrand && step.id === "insemination") {
        data["catheterBrand"] = phase.inseminationInfo.catheterBrand;
      }
      if (
        phase.inseminationInfo.ultrasoundGuided != null &&
        step.id === "insemination"
      ) {
        data["ultrasoundGuided"] = phase.inseminationInfo.ultrasoundGuided
          ? "yes"
          : "no";
      }
      if (
        phase.inseminationInfo.inseminationDuration != null &&
        step.id === "insemination"
      ) {
        data["inseminationDuration"] =
          phase.inseminationInfo.inseminationDuration;
      }
      if (phase.inseminationInfo.date && step.id === "insemination") {
        const time = phase.inseminationInfo.time
          ? `T${phase.inseminationInfo.time}:00`
          : "";
        data["procedureDate"] = `${phase.inseminationInfo.date}${time}`;
      }
      if (
        phase.inseminationInfo.notes &&
        (step.id === "fertilization" || step.id === "insemination")
      ) {
        data["notes"] = phase.inseminationInfo.notes;
      }
    }
    if (phase.eggRetrievalInfo?.notes && step.id === "egg_retrieval") {
      data["notes"] = phase.eggRetrievalInfo.notes;
    }
    // Retrieval used to seed `procedureDate` from `phase.startDate` here, to
    // give downstream planning logic a buffered date (#3273). The block below
    // already does that for every procedure step — and does it with the hour,
    // from `phase.startTime`. Seeding it early only got there first with a
    // day-resolution value, and the `data[dateFieldName] == null` guard below
    // then treated the field as already answered: the hour she had entered in
    // her appointment was dropped on every refetch, so the retrieval outcome
    // form asked for an oocyte count under an empty time field. Removed rather
    // than taught to read the time, so retrieval takes the same mapping every
    // other procedure step already took. That mapping is not yet the only place
    // this codebase assembles a date and an hour — `sperm_collection` and
    // `insemination` each still build their own below, from their own
    // sub-objects, and the appointment mirror in `CycleDetailScreen` builds a
    // third. Those are untouched here.
    if (step.id === "trigger_shot" && phase.triggerShot) {
      const ts = phase.triggerShot;
      if (ts.date) {
        const time = ts.time ?? "00:00";
        // No "Z" suffix — time is stored as local time, not UTC
        data["triggerDate"] = `${ts.date}T${time}:00`;
      }
      if (ts.notes) {
        data["triggerMedication"] = ts.notes;
      }
    }
    const embryos = embryoGradesFromPhase(phase.embryoData);
    if (embryos.length > 0) {
      data[EMBRYO_GRADES_FIELD] = encodeEmbryoGrades(embryos);
      // The single grade the step still shows is the best of them, worked
      // out here and never stored.
      const best = bestEmbryoGrade(
        embryos,
        step.fields?.find((field) => field.name === "embryoGrade")?.options ??
          [],
      );
      if (best) data["embryoGrade"] = best.grade;
    }
    if (step.id === "transfer" && phase.embryoTransferInfo) {
      if (phase.embryoTransferInfo.embryoType) {
        data["embryoType"] = phase.embryoTransferInfo.embryoType;
      }
      if (phase.embryoTransferInfo.notes != null) {
        data["notes"] = phase.embryoTransferInfo.notes;
      }
    }

    // PGT is currently stored as a typed genetics test result on the shared
    // embryo-development phase. Its lifecycle must not reuse that phase's
    // date status: a skipped PGT must not skip embryo development, and a
    // reported PGT must not manufacture embryo-development completion.
    if (step.id === "pgt_testing") {
      const pgt = phase.testResults?.find(
        (result) => result.name?.toLowerCase() === "pgt",
      );
      const pgtValue = pgt?.patientValue;
      const pgtStatus =
        pgtValue === "not_performed"
          ? "skipped"
          : pgtValue === "pending"
            ? "pending"
            : pgt
              ? "completed"
              : "pending";
      const parsedEuploidCount = numericValue(pgtValue);
      if (parsedEuploidCount != null) {
        data.euploidCount = parsedEuploidCount;
      }
      if (pgt?.notes) data.pgtNotes = pgt.notes;
      if (pgt?.attachment?.uploadId) {
        data.pgtResultUploadId = pgt.attachment.uploadId;
        data.pgtResultDocId = pgt.attachment.uploadId;
      }
      return {
        stepId: step.id,
        phaseId: phase.id,
        status: pgtStatus,
        completedAt: pgtStatus === "completed" ? (pgt?.date ?? null) : null,
        scheduledFor: null,
        data,
      };
    }

    // Map startDate + startTime to the step's date field
    if (
      (step.id === "egg_retrieval" ||
        step.id === "transfer" ||
        step.id === "fertilization" ||
        step.id === "insemination" ||
        step.id === "sperm_collection") &&
      phase.startDate
    ) {
      // Combine date + time — no "Z" suffix since time is stored as local time
      const dateFieldName =
        step.id === "transfer"
          ? "transferDate"
          : step.id === "sperm_collection"
            ? "collectionDate"
            : "procedureDate";
      if (data[dateFieldName] == null) {
        data[dateFieldName] = resolvePhaseProcedureDateValue({
          startDate: phase.startDate,
          startTime: phase.startTime,
        });
      }
    }

    // trigger_shot shares the "protocol" phase with stimulation.
    // Its status is based on whether triggerShot data exists, not the phase endDate.
    if (step.id === "trigger_shot") {
      const triggerDate = phase.triggerShot?.date ?? null;
      const hasTriggerData = !!triggerDate;
      const triggerScheduled = hasTriggerData && triggerDate! > todayIso;
      return {
        stepId: step.id,
        phaseId: phase.id,
        status: triggerScheduled
          ? "scheduled"
          : hasTriggerData
            ? "completed"
            : "pending",
        completedAt:
          hasTriggerData && !triggerScheduled ? (triggerDate ?? null) : null,
        scheduledFor: triggerScheduled ? triggerDate : null,
        data,
      };
    }

    // freezing (Outcome).
    //
    // In egg_freezing the freezing step shares the "egg_retrieval" phase
    // with the egg_retrieval step — completing the retrieval shouldn't also
    // mark the freezing step done. So for that flow we only mark freezing
    // complete when retrieval-result data (matureEggs / eggsFrozen) exists.
    //
    // In sperm_freezing the freezing step has its own phase (no other step
    // in that flow maps to "egg_retrieval"), and the relevant data fields
    // (vialsFrozen / freezeDate) aren't read back via fertilityMetrics —
    // they're round-tripped via phase.notes as a JSON blob written by
    // CycleDetailScreen.handleUpdateStepProgress. Fall back to the standard
    // endDate-based status so the user can advance to the completion view.
    if (step.id === "freezing") {
      const hasOtherEggRetrievalStep = steps.some(
        (s) => s.id === "egg_retrieval",
      );
      if (hasOtherEggRetrievalStep) {
        const hasFreezeData =
          data.matureEggs != null || data.eggsFrozen != null;
        return {
          stepId: step.id,
          phaseId: phase.id,
          status: hasFreezeData ? "completed" : "pending",
          completedAt: hasFreezeData ? (phase.endDate ?? null) : null,
          scheduledFor: null,
          data,
        };
      }
      // Standalone freezing step (e.g. sperm_freezing) — merge any JSON-
      // encoded step data stashed in phase.notes back into `data` so the
      // form's localData repopulates after a refetch.
      const mergedData = mergeNotesJson(data, phase.notes);
      return {
        stepId: step.id,
        phaseId: phase.id,
        status: isSkipped
          ? "skipped"
          : isScheduled
            ? "scheduled"
            : isCompleted
              ? "completed"
              : "pending",
        completedAt: phase.endDate ?? null,
        scheduledFor,
        data: mergedData,
      };
    }

    // sperm_freezing's `analysis` and `sperm_collection` steps persist
    // numeric/text values via the JSON-on-notes pattern (no fertilityMetric
    // mapping for abstinenceDays). Merge them back so the form's localData
    // hydrates correctly.
    //
    // The analysis figures also have real spermiogram columns now, and those
    // are laid over the blob rather than under it: they are what a clinic
    // writes to, so a result filed there after her last save is the newer
    // record. `analysisDataFromSpermiogram` returns only the columns that hold
    // something, so a row saved before those writes existed still hydrates from
    // the blob.
    if (step.id === "analysis" || step.id === "sperm_collection") {
      const mergedData =
        step.id === "analysis"
          ? {
              ...mergeNotesJson(data, phase.notes),
              ...analysisDataFromSpermiogram(
                analysisRowForDisplay(phase.spermiograms),
              ),
            }
          : mergeNotesJson(data, phase.notes);
      return {
        stepId: step.id,
        phaseId: phase.id,
        status: isSkipped
          ? "skipped"
          : isScheduled
            ? "scheduled"
            : isCompleted
              ? "completed"
              : "pending",
        completedAt: phase.endDate ?? null,
        scheduledFor,
        data: mergedData,
      };
    }

    return {
      stepId: step.id,
      phaseId: phase.id,
      status: isSkipped
        ? "skipped"
        : isScheduled
          ? "scheduled"
          : isCompleted
            ? "completed"
            : "pending",
      completedAt: phase.endDate ?? null,
      scheduledFor,
      data,
    };
  });
}

// ============================================================================
// Query Keys
// ============================================================================

// Shares the root key with `useCyclePhases` in src/packages/cycles/hooks/useCycles.ts
// so the care header and treatment hub read from a single cache entry per cycle.
// Invalidating `["cycle-phases"]` refreshes both surfaces.
export const phaseKeys = {
  // Derived from the canonical factory (#2240) so usePhases and the cycles
  // package's useCyclePhases share one cache entry per cycle.
  all: cycleKeys.phasesAll,
  cycle: (tenantId: string, cycleId: string) =>
    cycleKeys.phases(tenantId, cycleId),
};

// ============================================================================
// Hooks
// ============================================================================

/**
 * Fetch all phases for a cycle.
 * Pass undefined/null cycleId to disable the query.
 */
export function usePhases(cycleId: string | null | undefined) {
  const { client, tenantId } = useApiClient();

  const query = useQuery<ApiPhase[]>({
    queryKey: phaseKeys.cycle(tenantId ?? "", cycleId ?? ""),
    queryFn: async () => {
      if (!cycleId || !tenantId) return [];

      try {
        const response = await client.GET("/{tenantId}/cycles/{id}/phases", {
          params: {
            path: { tenantId, id: cycleId },
          },
        });

        if (response.error) {
          // An outage or authorization failure must not masquerade as a
          // cycle with no phases (#2239): consumers would prompt duplicate
          // phase creation or show an empty treatment history as truth.
          throwApiError(
            response.error,
            "Failed to fetch phases",
            "usePhases",
            response.response?.status,
          );
        }

        return response.data?.data ?? [];
      } catch (error) {
        throwApiError(error, "Failed to fetch phases", "usePhases", undefined);
      }
    },
    enabled: !!cycleId && !!tenantId,
    staleTime: 30000,
  });

  if (DEV_ACTIVE_CYCLE_OVERRIDE && cycleId === MOCK_CYCLE_ID) {
    return {
      ...query,
      data: MOCK_CYCLE_PHASES,
      isLoading: false,
      isFetching: false,
      isSuccess: true,
      isError: false,
      error: null,
    } as typeof query;
  }

  if (isDevNavigableCycleId(cycleId)) {
    return {
      ...query,
      data: MOCK_CYCLE_PHASES.map((phase) => ({
        ...phase,
        cycleId,
      })),
      isLoading: false,
      isFetching: false,
      isSuccess: true,
      isError: false,
      error: null,
    } as typeof query;
  }

  return query;
}

/**
 * Create a new phase for a cycle.
 * Used when a step is started or completed without a pre-existing phase.
 */
export function useCreatePhase() {
  const { client, tenantId } = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      cycleId,
      body,
    }: {
      cycleId: string;
      body: {
        type: string;
        startDate: string;
        startTime?: string | null;
        endDate?: string | null;
        fertilityMetrics?: { type: string; value: number | null }[];
      } & PhaseSubObjects;
    }): Promise<ApiPhase> => {
      if (!tenantId) throw new Error("Missing tenantId");

      const response = await client.POST("/{tenantId}/cycles/{id}/phases", {
        params: {
          path: { tenantId, id: cycleId },
        },
        body: toCreatePhaseBody(body),
      });

      if (response.error) {
        throwApiError(
          response.error,
          "Failed to create phase",
          "useCreatePhase",
          response.response?.status,
        );
      }

      return response.data!.data;
    },
    onSuccess: (created, variables) => {
      // Push the created phase into the cache synchronously so a follow-up
      // read via `queryClient.getQueryData(...)` in the same microtask sees
      // it. Without this, a two-step flow (schedule → log outcome) reads
      // stale cache, misses the new phase, and calls createPhase again —
      // Postgres rejects the second insert with a unique-key conflict.
      const key = phaseKeys.cycle(tenantId ?? "", variables.cycleId);
      queryClient.setQueryData<ApiPhase[]>(key, (prev) => {
        if (!prev) return [created];
        return prev.some((p) => p.id === created.id)
          ? prev
          : [...prev, created];
      });
      queryClient.invalidateQueries({ queryKey: key });
    },
  });
}

/**
 * Update an existing phase.
 * Used to mark a phase as completed (set endDate) or update fertility metrics.
 */
export function useUpdatePhase() {
  const { client, tenantId } = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      cycleId,
      phaseId,
      body,
    }: {
      cycleId: string;
      phaseId: string;
      body: {
        startDate?: string | null;
        startTime?: string | null;
        endDate?: string | null;
        /**
         * The contract's own union: a row carries a value, or it carries
         * `op: "delete"` and nothing but its id.
         *
         * A withdrawal has to remove the row rather than null it, because a
         * null row still reads back as "I don't know" — which would re-select
         * the toggle she had just turned off.
         */
        fertilityMetrics?: (
          | { id?: string; type: string; value: number | null }
          | { id: string; op: "delete" }
        )[];
      } & PhaseSubObjects;
    }): Promise<ApiPhase> => {
      if (!tenantId) throw new Error("Missing tenantId");

      const response = await client.PATCH(
        "/{tenantId}/cycles/{id}/phases/{phaseId}",
        {
          params: {
            path: { tenantId, id: cycleId, phaseId },
          },
          body: toUpdatePhaseBody(body),
        },
      );

      if (response.error) {
        throwApiError(
          response.error,
          "Failed to update phase",
          "useUpdatePhase",
          response.response?.status,
        );
      }

      return response.data!.data;
    },
    onSuccess: (updated, variables) => {
      // Mirror useCreatePhase: patch the cache synchronously so a follow-up
      // read in the same microtask reflects the new endDate/notes/metrics.
      const key = phaseKeys.cycle(tenantId ?? "", variables.cycleId);
      queryClient.setQueryData<ApiPhase[]>(key, (prev) =>
        prev ? prev.map((p) => (p.id === updated.id ? updated : p)) : prev,
      );
      queryClient.invalidateQueries({ queryKey: key });
    },
  });
}
